import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import Modal from '../../widgets/Modal';
import Button from '../../widgets/Button';
import { useWidgetsModalContext } from '../../../shared/providers/WidgetsModalProvider';

import {
  FeedbackRating,
  FeedbackRatingPills,
} from '../../../shared/constants/Feedback';
import {
  FeedbackModalProps,
  RatingPoint,
} from '../../../shared/types/Feedback';
import { submitManagerFeedback } from '../../../shared/service/widgetApi';
import { addItemToSessionStorage } from '../../../utilities/common/storage';
import { StorageItems } from '../../../shared/constants/app';
import {
  EventActions,
  EventCategories,
  EventNames,
} from '../../../shared/constants/analytics';
import useTracking from '../../../utilities/hooks/useTracking';
import TextArea from '../../widgets/TextArea';

function FeedbackModal({ open }: FeedbackModalProps) {
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedRating, setSelectedRating] = useState(FeedbackRating[2]);
  const [selectedRatingPoints, setSelectedRatingPoints] = useState<
    RatingPoint[]
  >([]);

  const { setFeedbackPopupOpen, setFeedbackSuccessOpen } =
    useWidgetsModalContext();
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  const title = t('SUBMIT_FEEDBACK');
  const subtitle = t('SUBMIT_FEEDBACK_SUBHEADING');
  const textAreaPlaceholder = t('WRITE_ADDITIONAL_INFO');

  const closeModal = () => setFeedbackPopupOpen(false);
  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const inputText = event.target.value;
    if (inputText.length <= 500) {
      setFeedbackText(inputText);
    }
  };

  const handleRatingSelection = (rating: (typeof FeedbackRating)[number]) => {
    setSelectedRating(rating);
    setSelectedRatingPoints([]);
  };

  const handleRatingPointSelection = (point: RatingPoint) => {
    const isSelected = selectedRatingPoints.some((p) => p.id === point.id);

    if (isSelected) {
      setSelectedRatingPoints(
        selectedRatingPoints.filter((p) => p.id !== point.id),
      );
    } else {
      setSelectedRatingPoints([...selectedRatingPoints, point]);
    }
  };

  const feedbackData: any = {
    experience: t(selectedRating.name),
    features: selectedRatingPoints.map((item) => t(item.name)).join(', '),
    comments: feedbackText,
  };

  const { isPending, mutate } = useMutation({
    mutationKey: ['managerFeedback'],
    mutationFn: () => submitManagerFeedback(feedbackData),
    onSuccess: async (res: any) => {
      if (res?.success) {
        addItemToSessionStorage(StorageItems.FEEDBACK_MODAL_CLOSE, 'true');
        closeModal();
        setFeedbackSuccessOpen(true);
      }
    },
    onError: () => {},
  });

  return (
    <Modal
      open={open}
      title={title}
      subTitle={subtitle}
      closeButton
      modalClassName="w-[640px]"
      handleClose={closeModal}
    >
      <div className="mt-5">
        <p className="text-black-700 text-sm font-medium">
          {t('SUBMIT_FEEDBACK_QUESTION1')}
          <label className="text-primary-dark">*</label>
        </p>
        <div className="mt-3 grid grid-cols-3 gap-x-2 md:gap-x-[27px]">
          {FeedbackRating.map((item) => (
            <div
              key={item.name}
              className={`flex cursor-pointer flex-col content-center items-center justify-center rounded-xl border transition-all duration-150 select-none ${
                selectedRating === item
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-black-200'
              } py-[14px]`}
              onClick={() => handleRatingSelection(item)}
            >
              <img src={item.icon} alt="" />
              <span
                className={`flex h-6 items-center justify-center text-center text-[13px] md:text-sm ${
                  selectedRating === item
                    ? 'text-primary-600 font-semibold'
                    : 'text-black-700'
                }`}
              >
                {t(item.name)}
              </span>
            </div>
          ))}
        </div>
        <div>
          <p className="text-black-700 mt-6 mb-3 text-sm font-medium">
            {selectedRating.key === 'bad'
              ? t('SUBMIT_FEEDBACK_QUESTION2')
              : selectedRating.key === 'ok'
                ? t('SUBMIT_FEEDBACK_QUESTION2')
                : t('SUBMIT_FEEDBACK_QUESTION3')}{' '}
            {t('SELECT_MORE')}
            <label className="text-primary-dark">*</label>
          </p>
          <div className="grid flex-wrap gap-2 md:flex">
            {FeedbackRatingPills[selectedRating.key].map((item) => (
              <div
                key={item.id} // Assuming item has an id property
                className={`cursor-pointer content-center justify-center rounded-[37px] border transition-all duration-150 select-none ${
                  selectedRatingPoints.some((point) => point.id === item.id)
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-black-200'
                } px-4 py-[10px]`}
                onClick={() => handleRatingPointSelection(item)}
              >
                <span
                  className={`text-center text-sm ${
                    selectedRatingPoints.some((point) => point.id === item.id)
                      ? 'text-primary-600 font-semibold'
                      : 'text-black-700'
                  }`}
                >
                  {t(item.name)}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label
            htmlFor="feedbackRating"
            className="text-black-700 mt-6 mb-[6px] block text-sm font-medium"
          >
            {t('ADDITIONAL_COMMENTS')}
          </label>
          <TextArea
            id="feedbackRating"
            placeholder={textAreaPlaceholder}
            rows={4}
            value={feedbackText}
            onChange={handleInputChange}
          />
          <p className="text-black-600 text-sm transition-all duration-150">
            {feedbackText.length}/500 {t('CHARACTERS')}
            <br />
            <br />
            <label className="text-primary-dark">
              {t('MANDATORY_FIELDS')} *
            </label>
          </p>
          <Button
            className="mt-6"
            size="lg"
            isLoading={isPending}
            onClick={() => {
              mutate();
              trackEvent(EventNames.WellBeingDashboard, {
                eventAction: EventActions.click,
                eventCategory: EventCategories.submitFeedback,
                eventLabel: 'submit_feedback',
              });
            }}
            disabled={selectedRatingPoints.length === 0}
          >
            {t('SUBMIT_FEEDBACK')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default FeedbackModal;
